/**
 * Types for the Grokbot (Bot Operations) snapshot, hand-written from
 * docs/bot-ops-handoff/schema/data.schema.json. Field names are identical to the
 * schema. Anything the source files do not state is null: the UI renders null as
 * "Not stated in source" / "no data" and never invents a value.
 */

export type Company = 'lifescribe' | 'quill';
export type StatusColor = 'green' | 'yellow' | 'red';
export type Tier = 'exec' | 'coordinator' | 'bot';
export type DecisionValue = 'pending' | 'yes' | 'no' | 'instructed';
export type Priority = 'Decision' | 'High' | 'Medium' | 'Reviewed' | 'Next step' | 'Blocker' | 'Low' | 'Unscored';
export type ImplStatus = 'not_started' | 'queued' | 'in_progress' | 'done' | 'failed' | 'blocked';

export interface Prov {
  path: string;
  line?: number | null;
  heading?: string | null;
  field?: string | null;
  mtime_ict?: string | null;
}

export interface GaRef {
  id: string;
  title?: string | null;
  status?: string | null;
  due?: string | null;
}

export interface Review {
  id?: string;
  exec: string;
  verdict: 'agree' | 'alternative';
  qualifier?: string | null;
  item?: string | null;
  text?: string | null;
  ga?: GaRef[];
  awaiting_grant?: boolean;
  urgency?: number;
  prov?: Prov | null;
  stale?: boolean;
  bot_rec?: { text?: string | null; prov?: Prov | null } | null;
  grant_note?: { text?: string | null; prov?: Prov | null } | null;
  finding_prefix?: string | null;
}

export interface ActionDecision {
  value: DecisionValue;
  decided_by?: string | null;
  decided_at?: string | null;
  instruction_id?: string | null;
}

export interface ActionImplementation {
  status: ImplStatus;
  owner?: string | null;
  notes?: string | null;
  updated_at?: string | null;
}

/** The exec's reasoning for an item, as Grok Bot writes it (the schema allows any object; these are the fields it uses). */
export interface ExecReasoning { exec?: string | null; verdict?: 'agree' | 'alternative' | null; text?: string | null; prov?: Prov | null }

export interface ActionItem {
  id: string;
  kind: 'finding' | 'review' | 'next_step' | 'blocker';
  owner_slug: string;
  target_agent_id?: string | null;
  finding?: string | null;
  for?: string | null;
  text?: string | null;
  priority: Priority;
  priority_code?: 'P0' | 'P1' | 'P2' | null;
  score?: number | null;
  bucket?: string | null;
  expected_impact?: string | null;
  effort_risk?: string | null;
  /** Schema v4 (optional until Grok Bot sends it): quantified impact, effort, risk tier and confidence. */
  impact?: { metric?: string | null; low?: number | null; high?: number | null; unit?: string | null; horizon_days?: number | null; basis?: string | null } | null;
  effort?: { hours_low?: number | null; hours_high?: number | null; who?: string | null; basis?: string | null } | null;
  risk?: { tier?: 1 | 2 | 3 | null; reversible?: boolean | null; blast_radius?: string | null; rollback?: string | null } | null;
  confidence?: number | null;
  confidence_basis?: string | null;
  source?: string | null;
  source_date?: string | null;
  prov?: Prov | null;
  needs_grant_approval?: boolean;
  decidable: boolean;
  recommended_action?: string | null;
  recommended_by?: 'bot' | 'exec' | null;
  recommended_label?: string | null;
  recommended_prov?: Prov | null;
  bot_original?: string | null;
  exec_review?: 'AGREE' | 'ALTERNATIVE' | null;
  alternative_action?: string | null;
  review?: Review | null;
  decision: ActionDecision;
  implementation: ActionImplementation;
  gchat_post?: boolean;
  gchat_stakeholders?: string[];
  gchat_topic?: string | null;
  gchat_basis?: string | null;
  gchat_status?: string | null;
  /** The Chat post for this item as the server last saw it (from the overlay). */
  gchat_chat?: { status: 'queued' | 'posted' | 'failed' | 'blocked' | 'cancelled'; recipients: string[]; posted_at: string | null; error: string | null; attempts: number } | null;
  why?: {
    excerpt?: string | null;
    excerpt_lines?: string | null;
    excerpt_status?: string | null;
    linked_ids?: string[];
    exec_reasoning?: ExecReasoning | null;
  } | null;
}

export interface Finding {
  title: string;
  score?: number | null;
  bucket?: string | null;
  problem?: string | null;
  solution?: string | null;
  prov?: Prov | null;
}

export interface Bot {
  slug: string;
  agent_id: string;
  name: string;
  short: string;
  company: Company;
  tier: Tier;
  exec_label?: string | null;
  group: string;
  reports_to?: string | null;
  coordinator?: string | null;
  exec_leads?: string[];
  status_raw?: string | null;
  doing?: { text?: string | null; prov?: Prov | null; updated_ict?: string | null } | null;
  last_activity_ict?: string | null;
  active: boolean;
  color: StatusColor;
  awaiting_grant?: boolean;
  disagreement?: boolean;
  has_data?: boolean;
  blockers?: { text: string; prov?: Prov | null }[];
  recommendation?: { text?: string | null; for?: string | null; prov?: Prov | null } | null;
  findings?: Finding[];
  findings_total?: number;
  findings_file?: Prov | null;
  latest_output?: { title?: string | null; headings?: string[]; prov?: Prov | null } | null;
  newest_file?: Prov | null;
  roster_prov?: Prov | null;
  reviews?: Review[];
  actions?: ActionItem[];
  role_title?: string | null;
  reports_to_label?: string | null;
  profile_description?: string | null;
  profile_prov?: Prov | null;
  kpis?: Record<string, unknown> | null;
  activity?: BotActivity | null;
  // Full-detail only (GET /bots/:slug); stripped from the snapshot.
  jd_markdown?: string | null;
  jd_markdown_path?: string | null;
  jd_prov?: Prov | null;
  jd_sections?: { heading: string; kind: string }[];
  tasks?: BotTask[];
  recommendations?: Recommendation[];
  documents?: BotDocument[];
}

export interface Recommendation {
  id: string;
  title: string;
  finding?: string | null;
  recommended_action?: string | null;
  recommended_by?: 'bot' | 'exec' | null;
  priority_code?: 'P0' | 'P1' | 'P2' | null;
  score?: number | null;
  bucket?: string | null;
  date?: string | null;
  first_seen?: string | null;
  last_seen?: string | null;
  versions?: number;
  source?: Prov | null;
  history?: Prov[];
  verdict?: { status: 'agree' | 'alternative' | 'none'; exec?: string | null; text?: string | null; alternative_action?: string | null; prov?: Prov | null } | null;
  action_id?: string | null;
  in_current_findings?: boolean;
  decision?: { value: DecisionValue; decided_by?: string | null; decided_at?: string | null } | null;
  implementation?: { status: ImplStatus; owner?: string | null; notes?: string | null; updated_at?: string | null } | null;
  why?: { excerpt?: string | null; excerpt_lines?: string | null; excerpt_status?: string | null; linked_ids?: string[]; exec_reasoning?: ExecReasoning | null } | null;
}

export interface BotTask {
  title: string;
  status: string;
  started_at?: string | null;
  started_basis?: string | null;
  running_for?: string | null;
  running_for_basis?: string | null;
  source?: Prov | null;
}

export interface BotDocument {
  title: string;
  filename: string;
  path: string;
  type: string;
  ext?: string | null;
  size_bytes?: number | null;
  created_at?: string | null;
  created_basis?: string | null;
  modified_at?: string | null;
  version?: string | null;
  sha256: string;
  s3_key?: string | null;
  scan: { status: string; findings?: unknown[] };
}

export interface BotActivity {
  events_by_day: { date: string; events: number | null }[];
  events_by_hour_7d: { date: string; hours: number[] | null }[];
  events_7d?: number | null;
  events_30d?: number | null;
  sessions_30d?: number | null;
  cadence_hours?: number | null;
  cadence_basis?: string | null;
  last_work_at?: string | null;
  idle_hours?: number | null;
  idle_for?: string | null;
  jd_cadence?: { hours: number; keyword: string; basis: string } | null;
  idle_alert?: string | null;
  basis?: string | null;
}

export interface LedgerRow {
  id: string;
  title: string;
  struck?: boolean;
  bucket?: string | null;
  due?: string | null;
  status: string;
  owner?: string | null;
  linked?: string | null;
  notes?: string | null;
  prov?: Prov | null;
}

export interface AwaitingItem {
  bot: string;
  slug: string;
  company: Company;
  item: string;
  exec: string;
  verdict: 'agree' | 'alternative';
  text?: string | null;
  ga?: GaRef[];
  urgency?: number;
  prov?: Prov | null;
  bot_rec?: string | null;
  action_id?: string | null;
}

export interface ExecCategory {
  slugs: string[];
  basis: Record<string, string[]>;
}

export interface Snapshot {
  schema_version: string;
  generated_ict: string;
  source_root: string;
  active_window_hours: number;
  companies: Record<Company, { name: string; accent: string }>;
  bots: Bot[];
  exec_categories: Record<string, ExecCategory>;
  ledger: LedgerRow[];
  ledger_prov?: Prov | null;
  awaiting: AwaitingItem[];
  instructions: Instruction[];
  implementation_bot?: { slug: string; agent_id: string; queue_path?: string } | null;
  warnings: string[];
  gchat?: { people: string[]; domains: { topic: string; stakeholders: string[]; bots: string[] }[]; rules_prov?: Prov | null } | null;
  sync?: { snapshot_id?: string | null; generated_ict?: string | null; trigger?: string | null; cadence?: string | null } | null;
}

export type InstructionKind = 'instruction' | 'question' | 'override';
export type InstructionStatus = 'open' | 'in_progress' | 'done' | 'declined';
export type ReplyStatus = 'in_progress' | 'done' | 'blocked' | 'failed' | 'declined_scope' | 'queued';

export interface InstructionReply {
  text?: string | null;
  at: string;
  status?: ReplyStatus | null;
  result_path?: string | null;
  source?: string | null;
}

/** A thread with a bot (DASHBOARD.md 2.3). `unread` is computed by the server from grant_seen_at. */
export interface Instruction {
  id: string;
  thread_id: string;
  kind: InstructionKind;
  grant_instruction: string;
  target_bot_slug: string;
  target_agent_id?: string | null;
  related_action_id?: string | null;
  priority: 'P0' | 'P1' | 'P2';
  needs_grant_approval_to_execute?: boolean;
  created_at: string;
  bot_ack: { status: 'pending' | 'acked'; at: string | null };
  bot_reply: InstructionReply[];
  status: InstructionStatus;
  bridge_status?: string | null;
  grant_seen_at?: string | null;
  unread: boolean;
  transport: {
    delivered_at: string | null;
    bot_received_at: string | null;
    last_message_id: string | null;
    message_id: string | null;
    webhook_status: 'pending' | 'sent' | 'retry' | 'failed' | null;
    webhook_attempts: number;
  };
}

/** What GET /snapshot wraps around the data. */
export interface SnapshotMeta {
  sha256: string;
  generated_ict: string;
  received_at: string;
  source: 's3' | 'seed';
  /** True only for the super admin: the server decides, the page just obeys. */
  can_write?: boolean;
}

export const EXEC_ORDER = ['CEO', 'CFO', 'CRO', 'CMO', 'CPO', 'Security Lead'] as const;

// ---- Phase 4: audit, activity, scorecard, preferences ---------------------------------

export interface AuditRow {
  id: string;
  at: string;
  admin_id: string | null;
  admin_email: string | null;
  action: string;
  item_type: string | null;
  item_id: string | null;
  from_value: string | null;
  to_value: string | null;
  details: Record<string, unknown> | null;
}

export type ActivityType = 'document' | 'status' | 'recommendation' | 'verdict' | 'reply';

export interface ActivityEvent {
  at: string;
  type: ActivityType;
  title: string;
  path?: string | null;
  rec_id?: string | null;
  bot_slug: string;
  company: Company | null;
  instruction_id?: string | null;
  status?: string | null;
}

export interface ActivityData {
  feed: ActivityEvent[];
  coverage: { earliest_event_ict?: string | null; note?: string | null } | null;
  generated_ict: string | null;
}

export interface ScoreStats {
  recommendations: number;
  reviewed: number;
  reviewed_pct: number | null;
  agreed: number;
  agreed_pct: number | null;
  approved: number;
  approved_pct: number | null;
  done: number;
  implemented_pct: number | null;
  latency_minutes: { rec_to_verdict: number | null; verdict_to_grant: number | null; grant_to_done: number | null };
}

export interface LeaderRow {
  slug: string;
  name: string;
  company: Company;
  recommendations: number;
  reviewed: number;
  agreed_pct: number | null;
  events_7d: number | null;
  documents: number;
}

export interface Scorecard {
  generated_ict: string | null;
  companies: Partial<Record<Company, ScoreStats>>;
  execs: Record<string, ScoreStats>;
  leaderboard: LeaderRow[];
  trend: Partial<Record<Company, { date: string; new_recommendations: number }[]>>;
  basis: string;
}

export interface Prefs {
  last_seen_at: string | null;
  theme: 'light' | 'dark' | null;
  notify: { unread_badges: boolean; digest: boolean; idle_alerts: boolean };
}

export interface SavedView { id: string; name: string; query: string; created_at: string }
